import styled from 'styled-components';
import MaxWidthLayout from '../../../templates/MaxWidthLayout';
import { makeExternalUrl, trackAnalytics, toSlug, attachLocale } from '../../../../lib/utils';
import SymbolFormat from '../shared/SymbolFormat';
import { DATA_TRACK_TYPE_CTA } from '../../../../lib/constants';
import Button from '../Button/Button';

const HeaderWrapper = styled.div`
  padding: ${({ theme }) => theme.unit(3)};
  
  @media (min-width: ${(p) => p.theme.minWidthBreakpoint.desktop}px) {
    padding: ${({ theme }) => theme.unit(6)};
  }
`;

const HeaderSectionDiv = styled.div`
  background-color: ${(p) => p.theme.c.bg.primary};
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: ${({ theme }) => theme.unit(3)} ${({ theme }) => theme.unit(2)};

  @media (min-width: ${(p) => p.theme.minWidthBreakpoint.tablet}px) {
    padding: ${({ theme }) => theme.unit(3)};
  }
  
  @media (min-width: ${(p) => p.theme.minWidthBreakpoint.desktop}px) {
    padding: ${({ theme }) => theme.unit(6)} ${({ theme }) => theme.unit(3)};
  }
`;

const HeaderTitleTextDiv = styled.div`
  ${({ theme }) => theme.typographyRebrand.heading['3xl']}
  text-align: center;
  color: ${(p) => p.color || p.theme.c.text.primary};
  margin-bottom: ${(p) => p.theme.unit(3)};
`;

const HeaderSubtitleTextDiv = styled.div`
  ${({ theme }) => theme.typographyRebrand.body.lg}
  text-align: center;
  color: ${(p) => p.color || p.theme.c.text.primary};
  margin-bottom: ${(p) => p.theme.unit(3)};
`;

const HeaderBlocksDiv = styled.div`
  display: flex;
  justify-content: center;
  text-align: center;
  flex-direction: column;
  align-items: center;

  @media (min-width: ${(p) => p.theme.minWidthBreakpoint.tablet}px) {
    flex-direction: row;
  }

  gap: ${({ theme }) => theme.unit(6)};
`;

const BlockDiv = styled.div`
  flex-basis: 33%;
  img {
    max-width: 100%;
  }
`;

const BlockTitleDiv = styled.div`
  ${({ theme }) => theme.typographyRebrand.heading.xl}
  text-align: center;
  color: ${(p) => p.theme.c.text.primary};
  margin-top: ${(p) => (p.isMobile ? p.theme.unit(2) : p.theme.unit(3))};
`;

const BlockBodyDiv = styled.div`
  ${({ theme }) => theme.typographyRebrand.body.md}
  text-align: center;
  color: ${(p) => p.theme.c.text.primary};
  margin-top: ${(p) => p.theme.unit(1)};
`;

const ButtonDiv = styled.div`
  display: flex;
  flex-direction: ${(p) => (p.isMobile ? 'column' : 'row')};
  justify-content: center;
  margin-top: ${p => p.theme.unit(3)};
  gap: ${({ theme }) => theme.unit(2)};

  button {
    box-sizing: content-box;
  }
`;

const HeaderSection = (props) => {
  const {
    headerSectionData = {},
    isMobile = false,
    parentId = '',
    locale = 'en',
    pageStateModifiers = {},
  } = props;

  const {
    titleText = '',
    titleColor,
    subtitleText = '',
    subtitleColor,
    backgroundColor,
    blocks = [],
    primaryButtonText = '',
    primaryButtonDescriptiveText,
    primaryButtonUrl = '',
    primaryButtonTagSet = {},
    secondaryButtonText = '',
    secondaryButtonDescriptiveText,
    secondaryButtonUrl = '',
    secondaryButtonTagSet = {},
  } = headerSectionData;

  const headerBlocks = blocks.map((block, i) => {
    const imageUrl = makeExternalUrl(block.imageUrl);

    return (
      // eslint-disable-next-line react/no-array-index-key
      <BlockDiv isMobile={isMobile} key={`headerBlocks-BlockDiv-${i}`}>
        <img src={imageUrl} alt="" />
        <BlockTitleDiv isMobile={isMobile}>
          <SymbolFormat text={block.title} />
        </BlockTitleDiv>
        <BlockBodyDiv>
          <SymbolFormat text={block.body} />
        </BlockBodyDiv>
      </BlockDiv>
    );
  });

  let PrimaryButtonToUse;
  let SecondaryButtonToUse;
  let trackPrimary = {};
  let trackSecondary = {};

  if (primaryButtonText && primaryButtonUrl) {
    trackPrimary = trackAnalytics(parentId, toSlug(`header-section-${primaryButtonText}`), `#${primaryButtonUrl}`, primaryButtonTagSet);
    PrimaryButtonToUse = (
      <Button
        variant="primary"
        size="large"
        theme="light"
        buttonText={primaryButtonText}
        accessibleLabel={primaryButtonDescriptiveText}
        dataTrackType={DATA_TRACK_TYPE_CTA}
        {...trackPrimary}
      />
    );
  }

  if (secondaryButtonText && secondaryButtonUrl) {
    trackSecondary = trackAnalytics(parentId, toSlug(`header-section-${secondaryButtonText}`), attachLocale(secondaryButtonUrl, locale), secondaryButtonTagSet);
    SecondaryButtonToUse = (
      <Button
        variant="secondary"
        size="large"
        theme="light"
        buttonText={secondaryButtonText}
        accessibleLabel={secondaryButtonDescriptiveText}
        dataTrackType={DATA_TRACK_TYPE_CTA}
        {...trackSecondary}
      />
    );
  }

  return (
    <MaxWidthLayout backgroundColor={backgroundColor}>
      <HeaderWrapper>
        <HeaderSectionDiv>
          <HeaderTitleTextDiv isMobile={isMobile} color={titleColor}>
            <SymbolFormat text={titleText} />
          </HeaderTitleTextDiv>
          <HeaderSubtitleTextDiv isMobile={isMobile} color={subtitleColor}>
            <SymbolFormat text={subtitleText} />
          </HeaderSubtitleTextDiv>
          <HeaderBlocksDiv isMobile={isMobile}>
            { headerBlocks }
          </HeaderBlocksDiv>
          {(SecondaryButtonToUse || PrimaryButtonToUse) && (
            <ButtonDiv isMobile={isMobile}>
              { !isMobile && SecondaryButtonToUse }
              { PrimaryButtonToUse }
              { isMobile && SecondaryButtonToUse }
            </ButtonDiv>
          )}
        </HeaderSectionDiv>
      </HeaderWrapper>
    </MaxWidthLayout>
  );
};

export default HeaderSection;
